import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants'
import DairyQuery from '~/QueryHooks/DairyQuery'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { useAuth } from '~/routes/components/AuthProvider'
import { El as LayoutEl } from '~/components/Layout/style';
import notify from '~/components/Notify'
import Dialog from '~/components/Dialog'
import { Popup } from 'semantic-ui-react'
import CustomIcon from '~/components/CustomIcon'
import CreateEditOperatingForm from './CreateEditOperatingForm'

const getTableColumns = () => [
  {
    name: i18n.t('Abbreviation'),
    key: 'abbreviation'
  },
  {
    name: i18n.t('Designation'),
    key: 'name'
  },
  {
    name: i18n.t('Actions')
  }
];

const OperatingForm: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const displayAction = roleSlug === ADMIN_ROLE_SLUG || roleSlug === OPERATIVE_ROLE_SLUG
  const [drawer, setDrawerState] = useState({
    isVisible: false,
    id: null
  })
  const [selectedType, setSelectedType] = useState(null)
  const actions = [
    {
      popupMessage: i18n.t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit',
      displayedText: i18n.t('No actions')
    },
    {
      popupMessage: i18n.t('Delete'),
      type: 'icon',
      name: 'trash',
      size: 'large',
      actionType: 'delete',
      displayedText: i18n.t('No actions'),
      renderActions: getAction
    }
  ]

  function getAction(row, type) {
    return <Popup
      content={t('Delete')}
      trigger={<CustomIcon
        size='large'
        style={{ marginBottom: 2 }}
        onClick={() => setSelectedType(row)}
        name='trash' />}
    />
  }

  const onSuccess = () => {
    notify(t('Successfully deleted operating form'), 'success');
    fetchData();
  };

  const {
    isLoading: isBusyData,
    data: operatingForms,
    refetch: fetchData
  } = DairyQuery.getOperatingFormsList()

  useEffect(() => {
    fetchData()
  }, [t])

  const { isLoading: isStateDeleteLoading, mutate: deleteOperatingForm } = DairyQuery.deleteOperatingForm(
    onSuccess
  );

  async function onOperatingFormDelete() {
    deleteOperatingForm(selectedType?.id)
    setSelectedType(null)
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'edit':
        return setDrawerState({ isVisible: true, id: row?.id })
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      tableColumnHeaders={getTableColumns()}
      rows={operatingForms || []}
      total={operatingForms.length || 0}
      handleClickAction={handleActionClick}
      removePagination
      actions={displayAction ? actions : []}
    />
  }

  function renderActions() {
    return <LayoutEl.PageHeaderActionButton
      onClick={() => setDrawerState({ isVisible: true, id: null })}
    >{t('Add new operating form')}</LayoutEl.PageHeaderActionButton>
  }

  function renderEditCreateState() {
    return <CreateEditOperatingForm
      isVisible={drawer.isVisible}
      idOperatingForm={drawer.id}
      fetchData={fetchData}
      handleHiddenDrawer={() => setDrawerState({ isVisible: false, id: null })}
    />
  }

  function renderDeleteModal() {
    return <Dialog
      key='dialog-operating-form-delete'
      textHeader={t(`Delete operating form: ${selectedType?.name}`)}
      textClose={t('Close')}
      textContent={t('Are you sure that you want to delete {{operatingForm}} operating form?', { operatingForm: selectedType?.name })}
      textSubmit={t('Delete operating form')}
      handleClose={() => setSelectedType(null)}
      handleSubmit={onOperatingFormDelete}
      type='abort'
      isDialogOpen={Boolean(selectedType)}
      isLoading={isStateDeleteLoading}
    />
  }

  return <Layout
    title={t('Operating Forms')}
    isBusy={isBusyData}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    actions={displayAction && renderActions()}
    useSpinner
  >
    {selectedType && renderDeleteModal()}
    {renderEditCreateState()}
    {operatingForms && renderTable()}
  </Layout>
}
export default OperatingForm
